
import * as ReactRouterDom from "react-router-dom";
import { ApiResponseData, BaseService, IGallery, ITeamMember, ITeamNews, ITeamWebsite, ITournamentEdition, ITournamentEditionMatch, OmneHostService, SanitizedResponse, TeamTypesValue } from "../../../../services";
import styles from "../assets/style.module.css";
import { Classname, StringHelper } from "@rivtn/noseur";
import { Utils } from "../../../../utils";
import React from "react";
import { buildMatchView } from "./Schedule";
import { buildGalleryView } from "./Gallery";
import { MemberCard, MemberModal } from "./Members";




type MemberStatus = "active" | "past";
type MemberRole = "MODERATOR" | "OWNER" | "member";
type MatchStatus = "upcoming" | "ongoing" | "past";
type MatchResult = "win" | "loss" | "draw";
type TournamentStatus = "active" | "past";
type LanguageCode = "en" | "pt" | "es" | "fr" | "de";
type ScheduleTab = "all" | MatchStatus;
type ActivityTab = "tournaments" | "feed";

interface Language {
    code: LanguageCode;
    label: string;
    flag: string;
}

interface MemberSocial {
    instagram?: string;
    twitter?: string;
    tiktok?: string;
}

interface Member {
    id: number;
    name: string;
    display: string;
    role: MemberRole;
    country: string;
    state: string;
    joined: string;
    left: string | null;
    status: MemberStatus;
    passport: string;
    half: string;
    achievements: string[];
    social: MemberSocial;
    profile: string;
}

interface Match {
    id: number;
    status: MatchStatus;
    date: string;
    opponent: string;
    competition: string;
    home: boolean;
    score: string | null;
    result?: MatchResult;
}

interface TournamentEvent {
    time: string;
    evt: string;
}

interface TournamentDay {
    day: string;
    events: TournamentEvent[];
}

interface Tournament {
    id: number;
    status: TournamentStatus;
    name: string;
    logo: string;
    prize: string;
    playersPerTeam: number;
    host: string;
    location: string;
    banner: string;
    game: string;
    placement: string;
    schedule: TournamentDay[];
}

interface Activity {
    id: number;
    icon: string;
    text: React.ReactNode;
    time: string;
}

interface NewsArticle {
    id: number;
    cat: string;
    title: string;
    excerpt: string;
    date: string;
    img: string;
    body: string;
}

interface MerchItem {
    id: number;
    name: string;
    price: number;
    img: string;
    badge: string | null;
}

interface TeamInfo {
    name: string;
    tag: string;
    founded: number;
    city: string;
    manager: string;
    coach: string;
    bio: string;
    stats: Record<string, number>;
    social: Record<string, string>;
}

interface GalleryItem {
    id: number;
    url: string;
    tag: string;
}

interface Achievement {
    year: number;
    title: string;
    icon: string;
    desc: string;
}

interface Trophy {
    id: number;
    name: string;
    competition: string;
    year: number;
    icon: string;
    tier: "gold" | "silver" | "bronze" | "special";
    description: string;
    img: string;
    game: string;
}

interface RankingEntry {
    rank: number;
    team: string;
    flag: string;
    country: string;
    game: string;
    wins: number;
    losses: number;
    draws: number;
    points: number;
    gd: number;
    form: ("W" | "L" | "D")[];
    isOurs: boolean;
}

// ─── STATIC DATA ──────────────────────────────────────────────────────────────

const LANGUAGES: Language[] = [
    { code: "en", label: "English", flag: "🇬🇧" },
    { code: "pt", label: "Português", flag: "🇧🇷" },
    { code: "es", label: "Español", flag: "🇪🇸" },
    { code: "fr", label: "Français", flag: "🇫🇷" },
    { code: "de", label: "Deutsch", flag: "🇩🇪" },
];

const TEAM: TeamInfo = {
    name: "FC VANGUARD", tag: "VGD", founded: 2018, city: "São Paulo, Brazil",
    manager: "Carlos Mendez", coach: "Rafael Souza",
    bio: "FC Vanguard is a competitive football club built on tactical excellence, raw talent, and an unbreakable team spirit. Founded in São Paulo, we've risen from the regional leagues to continental contention.",
    stats: { wins: 142, losses: 38, draws: 24, trophies: 11, goals: 487, members: 26 },
    social: { instagram: "@fcvanguard", twitter: "@FC_Vanguard", youtube: "FC Vanguard TV", tiktok: "@fcvanguard" },
};

const MEMBERS: Member[] = [
    { id: 1, name: "Carlos Mendez", display: "El Capitan", role: "MODERATOR", country: "🇧🇷 Brazil", state: "São Paulo", joined: "Jan 2018", left: null, status: "active", passport: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=300&q=80", half: "https://images.unsplash.com/photo-1571945192063-40f7fd5f7a49?w=400&q=80", achievements: ["Best Manager 2024", "Liga Elite Champion 2023"], social: { instagram: "@carlosmendez", twitter: "@carlos_mgr" }, profile: "#" },
    { id: 2, name: "Rafael Souza", display: "Coach Rafa", role: "OWNER", country: "🇧🇷 Brazil", state: "Rio de Janeiro", joined: "Mar 2018", left: null, status: "active", passport: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&q=80", half: "https://images.unsplash.com/photo-1517466787929-bc90951d0974?w=400&q=80", achievements: ["Tactical Excellence Award 2024"], social: { instagram: "@rafasouza_coach" }, profile: "#" },
    { id: 3, name: "Marco Vidal", display: "MV10", role: "member", country: "🇧🇷 Brazil", state: "São Paulo", joined: "Jun 2019", left: null, status: "active", passport: "https://images.unsplash.com/photo-1570295999919-56ceb5ecca61?w=300&q=80", half: "https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=400&q=80", achievements: ["Top Scorer 2024", "MVP Liga Elite 2023"], social: { instagram: "@marco.vidal10", twitter: "@marcovidal10" }, profile: "#" },
    { id: 4, name: "Lucas Ferreira", display: "The Wall", role: "member", country: "🇧🇷 Brazil", state: "Belo Horizonte", joined: "Aug 2019", left: null, status: "active", passport: "https://images.unsplash.com/photo-1568602471122-7832951cc4c5?w=300&q=80", half: "https://images.unsplash.com/photo-1508098682722-e99c643e7f0b?w=400&q=80", achievements: ["Best Defender 2024"], social: { instagram: "@lucaswall" }, profile: "#" },
    { id: 5, name: "Diego Santos", display: "Flash", role: "member", country: "🇧🇷 Brazil", state: "Fortaleza", joined: "Jan 2020", left: null, status: "active", passport: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&q=80", half: "https://images.unsplash.com/photo-1553778263-73a83bab9b0c?w=400&q=80", achievements: ["Fastest Player Award 2023"], social: { instagram: "@diegoflash9" }, profile: "#" },
    { id: 6, name: "Felipe Costa", display: "Maestro", role: "member", country: "🇧🇷 Brazil", state: "Recife", joined: "Mar 2020", left: null, status: "active", passport: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=300&q=80", half: "https://images.unsplash.com/photo-1543326727-cf6c39e8f84c?w=400&q=80", achievements: ["Most Assists 2024"], social: { twitter: "@felipemaestro" }, profile: "#" },
    { id: 7, name: "André Lima", display: "The Lion", role: "member", country: "🇦🇷 Argentina", state: "Buenos Aires", joined: "Jul 2021", left: null, status: "active", passport: "https://images.unsplash.com/photo-1552058544-f2b08422138a?w=300&q=80", half: "https://images.unsplash.com/photo-1560272564-c83b66b1ad12?w=400&q=80", achievements: [], social: { instagram: "@andrelion7" }, profile: "#" },
    { id: 8, name: "Pedro Gomes", display: "Rocket", role: "member", country: "🇧🇷 Brazil", state: "São Paulo", joined: "Oct 2021", left: null, status: "active", passport: "https://images.unsplash.com/photo-1547425260-76bcadfb4f2c?w=300&q=80", half: "https://images.unsplash.com/photo-1529900748604-07564a03e7a6?w=400&q=80", achievements: ["Best Young Player 2023"], social: { instagram: "@rocket_pedro" }, profile: "#" },
    { id: 9, name: "Thiago Ramos", display: "Phantom", role: "member", country: "🇨🇴 Colombia", state: "Bogotá", joined: "Jan 2022", left: null, status: "active", passport: "https://images.unsplash.com/photo-1545996124-0501ebae84d0?w=300&q=80", half: "https://images.unsplash.com/photo-1551958219-acbc595d9e51?w=400&q=80", achievements: [], social: { tiktok: "@phantomthiago" }, profile: "#" },
    { id: 10, name: "Bruno Alves", display: "Iron Bruno", role: "member", country: "🇧🇷 Brazil", state: "Campinas", joined: "Mar 2018", left: "Dec 2022", status: "past", passport: "https://images.unsplash.com/photo-1521119989659-a83eee488004?w=300&q=80", half: "https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?w=400&q=80", achievements: ["Copa Brasil 2020"], social: { instagram: "@ironbruno" }, profile: "#" },
    { id: 11, name: "Mateus Oliveira", display: "El Rojo", role: "member", country: "🇲🇽 Mexico", state: "Mexico City", joined: "Jun 2020", left: "Aug 2023", status: "past", passport: "https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=300&q=80", half: "https://images.unsplash.com/photo-1624526267942-ab0ff8a3e972?w=400&q=80", achievements: [], social: { twitter: "@elrojo_mx" }, profile: "#" },
];

const MATCHES: Match[] = [
    { id: 1, status: "upcoming", date: "Mar 02, 2026", opponent: "Sporting FC", competition: "Champions League", home: true, score: null },
    { id: 2, status: "upcoming", date: "Mar 07, 2026", opponent: "Real Tigres", competition: "La Liga Elite", home: false, score: null },
    { id: 3, status: "ongoing", date: "Feb 28, 2026", opponent: "Dynamo United", competition: "Copa Elite", home: true, score: "2 – 1" },
    { id: 4, status: "past", date: "Feb 18, 2026", opponent: "Napoli Stars", competition: "Champions League", home: false, score: "3 – 2", result: "win" },
    { id: 5, status: "past", date: "Feb 11, 2026", opponent: "Celtic Knights", competition: "La Liga Elite", home: true, score: "4 – 0", result: "win" },
    { id: 6, status: "past", date: "Feb 04, 2026", opponent: "Bayern Fusion", competition: "Copa Elite", home: false, score: "1 – 2", result: "loss" },
    { id: 7, status: "past", date: "Jan 28, 2026", opponent: "Ajax Elite", competition: "Champions League", home: true, score: "2 – 2", result: "draw" },
    { id: 8, status: "past", date: "Jan 21, 2026", opponent: "Sevilla FC", competition: "La Liga Elite", home: false, score: "3 – 1", result: "win" },
    { id: 9, status: "past", date: "Jan 14, 2026", opponent: "Porto United", competition: "Copa Elite", home: true, score: "0 – 1", result: "loss" },
    { id: 10, status: "past", date: "Jan 07, 2026", opponent: "Benfica Stars", competition: "Champions League", home: false, score: "4 – 2", result: "win" },
    { id: 11, status: "past", date: "Dec 21, 2025", opponent: "Fiorentina FC", competition: "La Liga Elite", home: true, score: "2 – 0", result: "win" },
    { id: 12, status: "past", date: "Dec 14, 2025", opponent: "Inter Milan X", competition: "Copa Elite", home: false, score: "1 – 1", result: "draw" },
];

const TOURNAMENTS: Tournament[] = [
    { id: 1, status: "active", name: "Champions League Elite S5", logo: "🏆", prize: "$250,000", playersPerTeam: 11, host: "UEFA Federation", location: "Madrid, Spain", banner: "https://images.unsplash.com/photo-1461896836934-ffe607ba8211?w=800&q=80", game: "Football / 11v11", placement: "Quarter Finals", schedule: [{ day: "Day 1 — Mar 2", events: [{ time: "10:00", evt: "Opening Ceremony" }, { time: "14:00", evt: "Group Stage Match 1" }] }, { day: "Day 2 — Mar 3", events: [{ time: "15:00", evt: "Group Stage Match 2" }, { time: "19:00", evt: "Press Conference" }] }] },
    { id: 2, status: "active", name: "Copa Elite Spring 2026", logo: "🥇", prize: "$80,000", playersPerTeam: 11, host: "South American FA", location: "São Paulo, Brazil", banner: "https://images.unsplash.com/photo-1560272564-c83b66b1ad12?w=800&q=80", game: "Football / 11v11", placement: "Semi Finals", schedule: [{ day: "Day 1 — Mar 5", events: [{ time: "09:00", evt: "Registration" }, { time: "13:00", evt: "Round of 16" }] }] },
    { id: 3, status: "past", name: "Liga Elite Season 4", logo: "⭐", prize: "$150,000", playersPerTeam: 11, host: "Liga Federation", location: "Buenos Aires, Argentina", banner: "https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=800&q=80", game: "Football / 11v11", placement: "🏆 Champion", schedule: [] },
    { id: 4, status: "past", name: "World Team Cup 2025", logo: "🌍", prize: "$500,000", playersPerTeam: 11, host: "FIFA", location: "London, England", banner: "https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?w=800&q=80", game: "Football / 11v11", placement: "Runner-Up", schedule: [] },
];

const ACTIVITIES: Activity[] = [
    { id: 1, icon: "🏆", text: <>Team <strong>FC Vanguard</strong> won the Liga Elite Season 4 trophy</>, time: "Jan 15, 2026" },
    { id: 2, icon: "👤", text: <><strong>Thiago Ramos</strong> joined the team as a new member</>, time: "Jan 22, 2026" },
    { id: 3, icon: "⚽", text: <><strong>Marco Vidal</strong> scored a hat-trick vs Napoli Stars</>, time: "Feb 18, 2026" },
    { id: 4, icon: "🥇", text: <>Team qualified for <strong>Champions League Quarter Finals</strong></>, time: "Feb 20, 2026" },
    { id: 5, icon: "🚫", text: <><strong>Mateus Oliveira</strong> departed from the team</>, time: "Aug 30, 2023" },
    { id: 6, icon: "🎖️", text: <><strong>Lucas Ferreira</strong> won Best Defender of the Year award</>, time: "Dec 10, 2024" },
    { id: 7, icon: "🤝", text: <>Team signed <strong>Nike</strong> as official kit sponsor</>, time: "Nov 1, 2024" },
    { id: 8, icon: "📋", text: <><strong>Rafael Souza</strong> appointed as new Owner</>, time: "Mar 1, 2018" },
];

const ACHIEVEMENTS: Achievement[] = [
    { year: 2025, title: "Liga Elite Season 4 Champions", icon: "🏆", desc: "Dominated the season with 28W 4L 2D record." },
    { year: 2025, title: "Copa Elite Runner-Up", icon: "🥈", desc: "Reached the final for the second consecutive year." },
    { year: 2024, title: "Best Team Award", icon: "⭐", desc: "Named the most progressive club of the year." },
    { year: 2024, title: "Copa Brasil Champions", icon: "🏅", desc: "Won the domestic cup with a 3-0 final victory." },
    { year: 2023, title: "Liga Elite Season 3 Champions", icon: "🏆", desc: "Back-to-back league title win." },
];

const TROPHIES: Trophy[] = [
    { id: 1, name: "Liga Elite Season 4", competition: "La Liga Elite", year: 2025, icon: "🏆", tier: "gold", game: "Football", description: "Unbeaten at home for the entire season, finishing 8 points clear at the top.", img: "https://images.unsplash.com/photo-1579952363873-27f3bade9f55?w=400&q=80" },
    { id: 2, name: "Copa Brasil", competition: "Copa Brasil", year: 2024, icon: "🏆", tier: "gold", game: "Football", description: "Won on penalties after a 2-2 draw in a tense final against Dynamo United.", img: "https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?w=400&q=80" },
    { id: 3, name: "Liga Elite Season 3", competition: "La Liga Elite", year: 2023, icon: "🏆", tier: "gold", game: "Football", description: "Back-to-back league title. Highest goals scored in a single season — 94.", img: "https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=400&q=80" },
    { id: 4, name: "Regional Cup", competition: "South American Cup", year: 2023, icon: "🏆", tier: "gold", game: "Football", description: "First major continental title for the club after a dramatic 3-2 final.", img: "https://images.unsplash.com/photo-1560272564-c83b66b1ad12?w=400&q=80" },
    { id: 5, name: "Copa Elite Runner-Up", competition: "Copa Elite", year: 2025, icon: "🥈", tier: "silver", game: "Football", description: "Reached the final for the second consecutive year, losing 1-0 in extra time.", img: "https://images.unsplash.com/photo-1508098682722-e99c643e7f0b?w=400&q=80" },
    { id: 6, name: "World Team Cup Runner-Up", competition: "World Team Cup", year: 2024, icon: "🥈", tier: "silver", game: "Football", description: "Best ever World Team Cup finish. Lost the final to Bayern Fusion in extra time.", img: "https://images.unsplash.com/photo-1553778263-73a83bab9b0c?w=400&q=80" },
    { id: 7, name: "Champions League Semi", competition: "Champions League", year: 2024, icon: "🥉", tier: "bronze", game: "Football", description: "First Champions League semi-final appearance in club history.", img: "https://images.unsplash.com/photo-1543326727-cf6c39e8f84c?w=400&q=80" },
    { id: 8, name: "Copa Elite Third Place", competition: "Copa Elite", year: 2023, icon: "🥉", tier: "bronze", game: "Football", description: "Third place finish after a convincing 3-0 win in the third-place play-off.", img: "https://images.unsplash.com/photo-1529900748604-07564a03e7a6?w=400&q=80" },
    { id: 9, name: "Best Team of the Year", competition: "Federation Awards", year: 2024, icon: "⭐", tier: "special", game: "Football", description: "Named most progressive and best-run club of the year by the Football Federation.", img: "https://images.unsplash.com/photo-1517466787929-bc90951d0974?w=400&q=80" },
    { id: 10, name: "Fair Play Award", competition: "La Liga Elite", year: 2023, icon: "🎖️", tier: "special", game: "Football", description: "Awarded for fewest yellow cards and exemplary sportsmanship throughout the campaign.", img: "https://images.unsplash.com/photo-1551958219-acbc595d9e51?w=400&q=80" },
    { id: 11, name: "Youth Academy Cup", competition: "U21 National Cup", year: 2022, icon: "🌟", tier: "gold", game: "Football", description: "Inaugural academy side cups win, launching what became a pathway to the first team.", img: "https://images.unsplash.com/photo-1624526267942-ab0ff8a3e972?w=400&q=80" },
];

const RANKINGS: RankingEntry[] = [
    { rank: 1, team: "Bayern Fusion", flag: "🇩🇪", country: "Germany", game: "Champions League", wins: 22, losses: 3, draws: 5, points: 71, gd: +48, form: ["W", "W", "D", "W", "W"], isOurs: false },
    { rank: 2, team: "Napoli Stars", flag: "🇮🇹", country: "Italy", game: "Champions League", wins: 20, losses: 5, draws: 5, points: 65, gd: +31, form: ["W", "L", "W", "W", "D"], isOurs: false },
    { rank: 3, team: "FC Vanguard", flag: "🇧🇷", country: "Brazil", game: "Champions League", wins: 19, losses: 4, draws: 7, points: 64, gd: +29, form: ["W", "W", "W", "D", "W"], isOurs: true },
    { rank: 4, team: "Celtic Knights", flag: "🏴󠁧󠁢󠁳󠁣󠁴󠁿", country: "Scotland", game: "Champions League", wins: 18, losses: 6, draws: 6, points: 60, gd: +22, form: ["D", "W", "W", "L", "W"], isOurs: false },
    { rank: 5, team: "Sporting FC", flag: "🇵🇹", country: "Portugal", game: "Champions League", wins: 17, losses: 7, draws: 6, points: 57, gd: +18, form: ["W", "D", "L", "W", "W"], isOurs: false },
    { rank: 6, team: "Ajax Elite", flag: "🇳🇱", country: "Netherlands", game: "Champions League", wins: 16, losses: 8, draws: 6, points: 54, gd: +14, form: ["L", "W", "D", "W", "W"], isOurs: false },
    { rank: 7, team: "Dynamo United", flag: "🇺🇦", country: "Ukraine", game: "Champions League", wins: 15, losses: 9, draws: 6, points: 51, gd: +8, form: ["W", "L", "W", "L", "D"], isOurs: false },
    { rank: 8, team: "Porto United", flag: "🇵🇹", country: "Portugal", game: "Champions League", wins: 14, losses: 10, draws: 6, points: 48, gd: +4, form: ["D", "D", "W", "L", "W"], isOurs: false },
    { rank: 1, team: "FC Vanguard", flag: "🇧🇷", country: "Brazil", game: "La Liga Elite", wins: 21, losses: 4, draws: 5, points: 68, gd: +41, form: ["W", "W", "W", "W", "D"], isOurs: true },
    { rank: 2, team: "Real Tigres", flag: "🇲🇽", country: "Mexico", game: "La Liga Elite", wins: 19, losses: 5, draws: 6, points: 63, gd: +34, form: ["W", "D", "W", "L", "W"], isOurs: false },
    { rank: 3, team: "Sevilla FC", flag: "🇪🇸", country: "Spain", game: "La Liga Elite", wins: 18, losses: 6, draws: 6, points: 60, gd: +27, form: ["W", "W", "D", "W", "L"], isOurs: false },
    { rank: 4, team: "Fiorentina FC", flag: "🇮🇹", country: "Italy", game: "La Liga Elite", wins: 16, losses: 7, draws: 7, points: 55, gd: +20, form: ["L", "W", "W", "D", "W"], isOurs: false },
    { rank: 5, team: "Benfica Stars", flag: "🇵🇹", country: "Portugal", game: "La Liga Elite", wins: 15, losses: 9, draws: 6, points: 51, gd: +12, form: ["W", "D", "L", "W", "W"], isOurs: false },
    { rank: 6, team: "Inter Milan X", flag: "🇮🇹", country: "Italy", game: "La Liga Elite", wins: 13, losses: 10, draws: 7, points: 46, gd: +5, form: ["D", "L", "W", "D", "W"], isOurs: false },
    { rank: 1, team: "Dynamo United", flag: "🇺🇦", country: "Ukraine", game: "Copa Elite", wins: 20, losses: 4, draws: 6, points: 66, gd: +37, form: ["W", "W", "L", "W", "W"], isOurs: false },
    { rank: 2, team: "FC Vanguard", flag: "🇧🇷", country: "Brazil", game: "Copa Elite", wins: 18, losses: 6, draws: 6, points: 60, gd: +28, form: ["W", "D", "W", "L", "W"], isOurs: true },
    { rank: 3, team: "Bayern Fusion", flag: "🇩🇪", country: "Germany", game: "Copa Elite", wins: 16, losses: 7, draws: 7, points: 55, gd: +19, form: ["L", "W", "W", "D", "W"], isOurs: false },
    { rank: 4, team: "Ajax Elite", flag: "🇳🇱", country: "Netherlands", game: "Copa Elite", wins: 14, losses: 9, draws: 7, points: 49, gd: +11, form: ["D", "W", "L", "W", "D"], isOurs: false },
];

const GALLERY: GalleryItem[] = [
    { id: 1, url: "https://images.unsplash.com/photo-1517466787929-bc90951d0974?w=600&q=80", tag: "Match" },
    { id: 2, url: "https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=600&q=80", tag: "Training" },
    { id: 3, url: "https://images.unsplash.com/photo-1508098682722-e99c643e7f0b?w=600&q=80", tag: "Celebration" },
    { id: 4, url: "https://images.unsplash.com/photo-1553778263-73a83bab9b0c?w=600&q=80", tag: "Match" },
    { id: 5, url: "https://images.unsplash.com/photo-1560272564-c83b66b1ad12?w=600&q=80", tag: "Portrait" },
    { id: 6, url: "https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?w=600&q=80", tag: "Stadium" },
    { id: 7, url: "https://images.unsplash.com/photo-1543326727-cf6c39e8f84c?w=600&q=80", tag: "Training" },
    { id: 8, url: "https://images.unsplash.com/photo-1529900748604-07564a03e7a6?w=600&q=80", tag: "Match" },
    { id: 9, url: "https://images.unsplash.com/photo-1551958219-acbc595d9e51?w=600&q=80", tag: "Fans" },
];

const NEWS: NewsArticle[] = [
    { id: 1, cat: "Match Report", title: "Vanguard Dominate in Copa Elite Semi Final", excerpt: "A stunning second-half comeback sees FC Vanguard advance to the semi finals with a 4-2 victory over Dynamo United.", date: "Feb 28, 2026", img: "https://images.unsplash.com/photo-1517466787929-bc90951d0974?w=600&q=80", body: "FC Vanguard put in a masterclass performance on Wednesday evening, overcoming a tight first half to dominate the second period and secure a crucial 4-2 victory. Marco Vidal was instrumental, providing two assists and scoring the decisive fourth goal with a sublime long-range effort. Manager Carlos Mendez praised the team's resilience and tactical discipline after the game." },
    { id: 2, cat: "Transfer News", title: "Thiago Ramos Signs Multi-Year Contract", excerpt: "Colombian international Thiago Ramos has committed his future to FC Vanguard, signing a three-year deal.", date: "Feb 20, 2026", img: "https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=600&q=80", body: "The club is delighted to announce that Thiago 'Phantom' Ramos has signed a new multi-year contract with FC Vanguard. The 23-year-old Colombian has been a key figure since joining the club and has shown tremendous growth." },
    { id: 3, cat: "Team News", title: "Nike Partnership Extended Through 2028", excerpt: "FC Vanguard and Nike have agreed to extend their kit and sponsorship deal through the 2027/28 season.", date: "Feb 10, 2026", img: "https://images.unsplash.com/photo-1553778263-73a83bab9b0c?w=600&q=80", body: "In a landmark deal for the club, FC Vanguard has extended its partnership with global sportswear giant Nike through to the end of the 2027/28 season." },
    { id: 4, cat: "Match Preview", title: "Champions League Quarter Final Preview", excerpt: "FC Vanguard gear up for the biggest clash of their season against Sporting FC in the Champions League.", date: "Mar 01, 2026", img: "https://images.unsplash.com/photo-1560272564-c83b66b1ad12?w=600&q=80", body: "The quarter final of the Champions League Elite beckons and the squad is in top form going into the tie. Owner Rafael Souza has confirmed a clean bill of health for the squad." },
    { id: 5, cat: "Award", title: "Lucas Ferreira Named Best Defender 2024", excerpt: "Veteran defender Lucas Ferreira has been awarded the prestigious Best Defender of the Year award.", date: "Dec 10, 2024", img: "https://images.unsplash.com/photo-1508098682722-e99c643e7f0b?w=600&q=80", body: "Lucas 'The Wall' Ferreira has been recognised by the Football Federation as the best defender in the competition for 2024." },
    { id: 6, cat: "Youth", title: "Vanguard Academy Launches Scholarship Programme", excerpt: "FC Vanguard announces a scholarship programme to bring the next generation of talent through its renowned academy.", date: "Nov 15, 2024", img: "https://images.unsplash.com/photo-1551958219-acbc595d9e51?w=600&q=80", body: "FC Vanguard Academy is proud to announce the launch of its annual scholarship programme offering 10 full scholarships to talented youth players from the São Paulo region." },
];

const MERCH: MerchItem[] = [
    { id: 1, name: "Home Jersey 2025/26", price: 89.99, img: "https://images.unsplash.com/photo-1620794108219-aedbde4e6fb0?w=400&q=80", badge: "New" },
    { id: 2, name: "Training Kit Set", price: 64.99, img: "https://images.unsplash.com/photo-1599481238640-4c1288750d7a?w=400&q=80", badge: null },
    { id: 3, name: "Signed Match Ball", price: 199.99, img: "https://images.unsplash.com/photo-1579952363873-27f3bade9f55?w=400&q=80", badge: "Limited" },
    { id: 4, name: "VGD Team Hoodie", price: 54.99, img: "https://images.unsplash.com/photo-1556821840-3a63f15732ce?w=400&q=80", badge: null },
    { id: 5, name: "Away Jersey 2025/26", price: 89.99, img: "https://images.unsplash.com/photo-1571945192063-40f7fd5f7a49?w=400&q=80", badge: null },
    { id: 6, name: "VGD Cap Collection", price: 29.99, img: "https://images.unsplash.com/photo-1588850561407-ed78c282e89b?w=400&q=80", badge: "Hot" },
];

const SPONSORS: string[] = ["NIKE", "PEPSI", "EA SPORTS", "GATORADE", "HUBLOT", "BANCO ATLAS"];

interface HomeProps {
    basePath: string;
    publicId: string;
    website: ITeamWebsite;
}

function Home(props: HomeProps) {

    const { basePath, website, publicId } = props;
    const nameSplit = website.team.name.split(/\s+/);
    const [nameFirstWord, ...nameRemain] = nameSplit;
    const omnehostService = OmneHostService.getInstance();
    const [gallery, setGallery] = React.useState<IGallery[]>();
    const [members, setMembers] = React.useState<ITeamMember[]>();
    const [teamNews, setTeamNews] = React.useState<ITeamNews[]>();
    const [selectedMember, setSelectedMember] = React.useState<ITeamMember | null>(null);
    const [matches, setMatches] = React.useState<ApiResponseData<ITournamentEditionMatch>>();
    const [activeTournaments, setActiveTournaments] = React.useState<ITournamentEdition[]>();

    React.useEffect(fetchInformation, []);

    return (
        <>
            <section className={styles["hero"]}>
                <div className={styles["hero-bg"]} />
                <div className={styles["hero-grid-bg"]} />
                <div className={styles["hero-content"]}>
                    <div>
                        <div className={styles["hero-tag"]}>⚽ Official Team Website</div>
                        <h1 className={styles["hero-name"]}>{nameFirstWord}{(nameSplit?.length > 0) && (<><br /><em>{nameRemain.join(" ")}</em></>)}</h1>
                        {/* <p className={styles["hero-tagline"]}>Play Bold. Win Together.</p> */}
                        <p className={styles["hero-bio"]}>{website.team.description}</p>
                        <div className={styles["hero-stats"]}>
                            {(!!website.extra_data?.stats) && (Object.entries(website.extra_data?.stats).map(([k, v]) => (
                                <div key={k} className={styles["h-stat"]}>
                                    <span className={styles["h-stat-val"]}>{v as number}</span>
                                    <span className={styles["h-stat-lbl"]}>{k.replaceAll("_", " ")}</span>
                                </div>
                            )))}
                        </div>
                        <div className={styles["hero-cta"]}>
                            {(website.website_pages?.MERCH?.visible) && (<ReactRouterDom.NavLink end to={`/${basePath}/merch`} className={styles["btn-primary"]} style={{ textDecoration: "none" }}>Shop Merchandise</ReactRouterDom.NavLink>)}
                            {(website.website_pages?.MEMBERS?.visible) && (<ReactRouterDom.NavLink end to={`/${basePath}/members`} className={styles["btn-ghost"]} style={{ textDecoration: "none" }}>Meet the Squad</ReactRouterDom.NavLink>)}
                        </div>
                    </div>
                    <div className={styles["hero-player-wrap"]}>
                        <div className={styles["hero-player-glow"]} />
                        <img
                            className={styles["hero-player-img"]}
                            src={website.team.logo_url ?? website.media?.LOGO?.[0]}
                            alt={website.team.name}
                        />
                        {(!!members) && (<>
                            <div className={styles["hero-player-badge"]}>
                                <span className={styles["hero-player-badge-num"]}>{members?.length}</span>
                                <span className={styles["hero-player-badge-lbl"]}>Members</span>
                            </div>
                            <div className={styles["hero-player-name-tag"]}>
                                <strong>{members.find((m) => m.role === "OWNER")?.display_name}</strong>
                                <span>Owner</span>
                            </div>
                        </>)}
                    </div>
                </div>
                <div className={styles["hero-emblem"]}>{nameSplit.length > 1 ? nameSplit.map((s) => s[0]).join("") : website.team.name}</div>
            </section>

            {(website.home_settings?.show_sponsors && website.sponsors?.length) && (<div className={styles["section"]}>
                <div className={styles["sec-line"]} />
                <h2 className={styles["sec-title"]}>Our <em>Sponsors</em></h2>
                <p className={styles["sec-sub"]}>Partners powering our ambition</p>
                <div className={styles["sponsor-row"]}>
                    {website.sponsors.map((s) => (
                        <div key={s} className={styles["sponsor-chip"]}>
                            <img alt={s} src={s} />
                        </div>
                    ))}
                </div>
            </div>)}

            {(!!activeTournaments?.length) && (
                <div className={styles["section"]}>
                    <div className={styles["sec-line"]} />
                    <h2 className={styles["sec-title"]}>Active <em>Tournaments</em></h2>
                    <p className={styles["sec-sub"]}>Currently competing in</p>
                    {activeTournaments.map((t) => {
                        return (<ReactRouterDom.NavLink key={t.external_id} to={`/to/${t.tournament_id}`} target="_blank" className={Classname.build(styles["current-tournament"], styles["unlinked"])}>
                            <img className={styles["ct-banner"]} src={t.banner_url} alt={t.name} />
                            <div className={styles["ct-body"]}>
                                {/* <div className={styles["ct-logo"]}>{t.logo_url}</div> */}
                                <img className={styles["ct-logo"]} src={t.logo_url} alt={t.name} style={{ width: 70, borderRadius: "100%", marginRight: 5 }} />
                                <div className={styles["ct-info"]}>
                                    <div className={styles["ct-status"]}>● ACTIVE</div>
                                    <div className={styles["ct-name"]}>{t.name}</div>
                                    <div className={styles["ct-meta"]}>{t.location} · {StringHelper.toSaneSentenceFormat(Object.keys(TeamTypesValue)[t.participant_members_count] ?? "" + t.participant_members_count)} · {t.meta.host_name}</div>
                                </div>
                                <div>
                                    {(!!t.meta.price_pool_monetary) && (<div className={styles["ct-prize"]}>Monetary Prizes</div>)}
                                    {(!!t.meta.price_pool_physical) && (<div className={styles["ct-prize-lbl"]}>Physical Prizes</div>)}
                                </div>
                            </div>
                        </ReactRouterDom.NavLink>);
                    })}
                </div>
            )}

            <div className={styles["section"]} style={{ paddingTop: 0 }}>
                <div className={styles["sec-line"]} />
                <h2 className={styles["sec-title"]}>Recent <em>Achievements</em></h2>
                <p className={styles["sec-sub"]}>A club forged in silverware</p>
                <div className={styles["ach-strip"]}>
                    {ACHIEVEMENTS.map((a: Achievement) => (
                        <div key={a.title} className={styles["ach-chip"]}>
                            <div className={styles["ach-icon"]}>{a.icon}</div>
                            <div>
                                <div className={styles["ach-year"]}>{a.year}</div>
                                <div className={styles["ach-chip-title"]}>{a.title}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {(website.website_pages?.SCHEDULE?.visible && website.home_settings.show_schedules) && (<div className={styles["section"]} style={{ paddingTop: 0 }}>
                <div className={styles["sec-line"]} />
                <div className={styles["sec-header"]}>
                    <div>
                        <h2 className={styles["sec-title"]}>Recent <em>Matches</em></h2>
                        <p className={styles["sec-sub"]} style={{ marginBottom: 0 }}>Next fixtures</p>
                    </div>
                    <ReactRouterDom.NavLink end to={`/${basePath}/schedule`} className={styles["btn-ghost"]} style={{ marginBottom: "3rem", textDecoration: "none" }} >Full Schedule →</ReactRouterDom.NavLink>
                </div>
                <div style={{ marginTop: "2rem" }} className={styles["matches-brief"]}>
                    {matches?.content!.map((t) => buildMatchView(publicId, website, t))}
                </div>
            </div>)}

            {(website.website_pages?.MEMBERS?.visible) && (<div className={styles["section"]} style={{ paddingTop: 0 }}>
                <div className={styles["sec-line"]} />
                <div className={styles["sec-header"]}>
                    <div>
                        <h2 className={styles["sec-title"]}>Current <em>Squad</em></h2>
                        <p className={styles["sec-sub"]} style={{ marginBottom: 0 }}>Meet the players</p>
                    </div>
                    <ReactRouterDom.NavLink end to={`/${basePath}/news`} className={styles["btn-ghost"]} style={{ marginBottom: "3rem", textDecoration: "none" }} >View All →</ReactRouterDom.NavLink>
                </div>
                <div style={{ marginTop: "2rem" }} className={styles["members-grid"]}>
                    {members?.map((m: ITeamMember) => (
                        <MemberCard key={m.external_id} member={m} onSelect={setSelectedMember} />
                    ))}
                </div>
            </div>)}

            {(website.website_pages?.NEWS?.visible) && (<div className={styles["section"]} style={{ paddingTop: 0 }}>
                <div className={styles["sec-line"]} />
                <div className={styles["sec-header"]}>
                    <div>
                        <h2 className={styles["sec-title"]}>Recent <em>News</em></h2>
                        <p className={styles["sec-sub"]} style={{ marginBottom: 0 }}>Latest from the club</p>
                    </div>
                    <ReactRouterDom.NavLink end to={`/${basePath}/news`} className={styles["btn-ghost"]} style={{ marginBottom: "3rem", textDecoration: "none" }} >All News →</ReactRouterDom.NavLink>
                </div>
                <div style={{ marginTop: "2rem" }} className={styles["news-grid"]}>
                    {teamNews?.map((n: ITeamNews) => (
                        <ReactRouterDom.NavLink key={n.external_id} to={{ pathname: `/${basePath}/news`, search: "article=" + n.external_id }} className={Classname.build(styles["news-card"], styles["unlinked"])}>
                            {(!!n.extra_data?.featured_image) && (<img className={styles["news-card-img"]} src={n.extra_data.featured_image} alt={n.title} loading="lazy" />)}
                            <div className={styles["news-card-body"]}>
                                {(!!n.extra_data?.category) && (<div className={styles["news-cat"]}>{n.extra_data.category}</div>)}
                                <div className={styles["news-title"]}>{n.title}</div>
                                <div className={styles["news-excerpt"]}>{n.content.substring(0, 120)}</div>
                                <div className={styles["news-meta"]}><span>📅 {Utils.fineDate(new Date(n.updated_at))}</span></div>
                            </div>
                        </ReactRouterDom.NavLink>
                    ))}
                </div>
            </div>)}

            {(Object.keys(website.social_accounts ?? {}).length > 0) && (<div className={styles["section"]} style={{ paddingTop: 0 }}>
                <div className={styles["sec-line"]} />
                <h2 className={styles["sec-title"]}>Social <em>Networks</em></h2>
                <p className={styles["sec-sub"]}>Follow the journey</p>
                <div className={styles["social-grid"]}>
                    {Object.entries(website.social_accounts ?? {}).map(([social, username]) => (<ReactRouterDom.NavLink target="_blank" key={social} to={Utils.getSocialLink(social, username)} className={styles["social-card"]}>
                        <i className={Classname.build(styles["social-icon"], Utils.getSocialIcon(social))} />
                        <div className={styles["social-name"]}>{social}</div>
                        <div className={styles["social-handle"]}>{username}</div>
                    </ReactRouterDom.NavLink>))}
                </div>
            </div>)}

            {(website.website_pages?.GALLERY?.visible && website.home_settings.show_top_gallery) && (<div className={styles["section"]} style={{ paddingTop: 0 }}>
                <div className={styles["sec-line"]} />
                <div className={styles["sec-header"]}>
                    <div>
                        <h2 className={styles["sec-title"]}>Top <em>Gallery</em></h2>
                        <p className={styles["sec-sub"]} style={{ marginBottom: 0 }}>Behind the scenes</p>
                    </div>
                    <ReactRouterDom.NavLink end to={`/${basePath}/gallery`} className={styles["btn-ghost"]} style={{ marginBottom: "3rem", textDecoration: "none" }} >View All →</ReactRouterDom.NavLink>
                </div>
                <div style={{ marginTop: "2rem" }} className={`${styles["gallery-grid"]} ${styles["home-grid"]}`}>
                    {gallery?.map(buildGalleryView)}
                </div>
            </div>)}

            {selectedMember && <MemberModal member={selectedMember} onClose={() => setSelectedMember(null)} />}
        </>
    );

    function fetchInformation() {
        omnehostService.queryTeamGallery(website.team.public_id, {
            size: 10,
        }).then(({ content }: SanitizedResponse<IGallery>) => {
            setGallery(content);
        }).catch(BaseService.reportError);
        omnehostService.queryTeamSchedule(website.team.public_id, {
            size: 10,
            sort: ["start_date,ASC", "status,ASC"],
        }).then(({ apiResponse }: SanitizedResponse<ITournamentEditionMatch>) => {
            setMatches(apiResponse.data!);
        }).catch(BaseService.reportError);
        omnehostService.queryTeamMembers(props.website.team.public_id, {
            size: 9,
            status: "ACTIVE",
            sort: "role,DESC",
        }).then(({ content }: SanitizedResponse<ITeamMember>) => {
            setMembers(content);
        }).catch(BaseService.reportError);
        omnehostService.queryTeamNews(props.website.team.public_id, {
            size: 3,
            sort: "updated_at,DESC",
        }).then(({ content }: SanitizedResponse<ITeamNews>) => {
            setTeamNews(content);
        }).catch(BaseService.reportError);
        omnehostService.queryTeamTournaments(props.website.team.public_id, {
            page: 1,
            size: 5,
            status: "PAST", // TODO
        }).then(({ content }: SanitizedResponse<ITournamentEdition>) => {
            setActiveTournaments(content);
        }).catch(BaseService.reportError);
    }

}

export default Home;